import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FeatherDownload,
  FeatherUpload,
  FeatherTrash2,
  FeatherPlay,
  FeatherPause,
  FeatherX,
  FeatherCheck,
  FeatherAlertTriangle,
  FeatherClock,
  FeatherChevronDown,
  FeatherChevronRight,
  FeatherImage,
  FeatherInfo,
  FeatherFile,
  FeatherCopy,
} from '@subframe/core';
import { Button } from '../../ui/components/Button';
import { Badge } from '../../ui/components/Badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import {
  downloadMutationCSV,
  listMutationBatches,
  createMutationBatch,
  getBatchPreview,
  removeBatchItem,
  submitMutationBatch,
  cancelMutationBatch,
  resumeMutationBatch,
  pauseMutationBatch,
  createMutationFeePayment,
  checkMutationFeePaymentStatus,
  type MutationBatch,
  type PreviewItem,
  type BatchCreateResult,
  type MutationFeePayload,
} from '../../api/mutations';
import { useMutationImageUpload } from '../../hooks/useMutationImageUpload';
import { WalletSigningModal } from '../transactions/WalletSigningModal';
import { toSigningPayload } from '../../lib/wallet';

interface MutationsTabProps {
  projectSlug: string | null;
  collectionSlug: string;
  collectionName: string;
  showSuccess: (msg: string) => void;
  showError: (msg: string) => void;
}

type WizardStep = 'upload' | 'preview' | 'schedule';

const STATUS_BADGES: Record<string, { variant: string; label: string }> = {
  preparing: { variant: 'warning', label: 'Preparing' },
  ready: { variant: 'brand', label: 'Ready' },
  pending_payment: { variant: 'warning', label: 'Awaiting Payment' },
  scheduled: { variant: 'brand', label: 'Scheduled' },
  processing: { variant: 'warning', label: 'Processing' },
  paused: { variant: 'error', label: 'Paused' },
  completed: { variant: 'success', label: 'Completed' },
  cancelled: { variant: 'neutral', label: 'Cancelled' },
  failed: { variant: 'error', label: 'Failed' },
};

function formatTimeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ~1 second per NFT for NFTokenModify transactions
const SECONDS_PER_NFT = 1;

function estimatedDuration(nftCount: number): string {
  const seconds = nftCount * SECONDS_PER_NFT;
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainMin = minutes % 60;
  return `${hours}h ${remainMin}m`;
}

const MUTATION_FREE_TIER = 2; // TEMP: lowered for testing (normally 10)
const MUTATION_PER_NFT_FEE_XRP = 0.05;

function calculateMutationFeeClient(itemCount: number) {
  const billableCount = Math.max(0, itemCount - MUTATION_FREE_TIER);
  const feeXrp = Math.round(billableCount * MUTATION_PER_NFT_FEE_XRP * 100) / 100;
  return {
    feeXrp,
    perNftFeeXrp: MUTATION_PER_NFT_FEE_XRP,
    freeTier: MUTATION_FREE_TIER,
    billableCount,
  };
}

export const MutationsTab: React.FC<MutationsTabProps> = ({
  projectSlug,
  collectionSlug,
  collectionName,
  showSuccess,
  showError,
}) => {
  // Batch list state
  const [batches, setBatches] = useState<MutationBatch[]>([]);
  const [batchesLoading, setBatchesLoading] = useState(true);

  // CSV download state
  const [downloading, setDownloading] = useState(false);

  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState<WizardStep>('upload');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [batchName, setBatchName] = useState('');
  const [batchDescription, setBatchDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState<BatchCreateResult | null>(null);

  // Client-side CSV parsing state
  const [csvImageFilenames, setCsvImageFilenames] = useState<string[]>([]);
  const [csvRowCount, setCsvRowCount] = useState(0);
  const [showImageListModal, setShowImageListModal] = useState(false);

  // Image upload hook (presigned S3 URLs)
  const imageUpload = useMutationImageUpload({
    projectSlug,
    collectionSlug,
    batchId: createResult?.batchId || '',
    requiredFilenames: csvImageFilenames,
    onComplete: (result) => {
      showSuccess(`${result.uploaded} images uploaded successfully`);
      if (createResult) {
        setWizardStep('preview');
        loadPreview(createResult.batchId);
      }
    },
    onError: (error) => showError(error),
  });

  // Preview state
  const [previewItems, setPreviewItems] = useState<PreviewItem[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPage, setPreviewPage] = useState(1);
  const [previewTotal, setPreviewTotal] = useState(0);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [copiedTokenId, setCopiedTokenId] = useState<string | null>(null);

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdownMessage, setCountdownMessage] = useState('');

  // Payment state
  const [feePayload, setFeePayload] = useState<MutationFeePayload | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [waitingForPayment, setWaitingForPayment] = useState(false);
  const [paymentBatchId, setPaymentBatchId] = useState<string | null>(null);
  const paymentPollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const csvInputRef = useRef<HTMLInputElement>(null);

  // Load batches
  const loadBatches = useCallback(async () => {
    setBatchesLoading(true);
    try {
      const result = await listMutationBatches(projectSlug, collectionSlug);
      setBatches(result.batches);
    } catch (err) {
      console.error('Error loading batches:', err);
    } finally {
      setBatchesLoading(false);
    }
  }, [projectSlug, collectionSlug]);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  // Track previous batch statuses for completion detection
  const prevBatchStatusRef = useRef<Map<string, string>>(new Map());

  // Poll for updates when batches are processing or scheduled
  useEffect(() => {
    const hasActive = batches.some(b => b.status === 'processing' || b.status === 'scheduled');
    if (!hasActive) return;

    const interval = setInterval(() => {
      loadBatches();
    }, 5000);

    return () => clearInterval(interval);
  }, [batches, loadBatches]);

  // Detect batch completion transitions
  useEffect(() => {
    const prevStatuses = prevBatchStatusRef.current;

    for (const batch of batches) {
      const prevStatus = prevStatuses.get(batch.id);
      if (prevStatus === 'processing' && batch.status === 'completed') {
        showSuccess(`Batch "${batch.name || 'Bulk mutation'}" completed! ${batch.completed_count} NFTs mutated.`);
      }
    }

    // Update ref with current statuses
    const newMap = new Map<string, string>();
    for (const batch of batches) {
      newMap.set(batch.id, batch.status);
    }
    prevBatchStatusRef.current = newMap;
  }, [batches, showSuccess]);

  // Download CSV
  const handleDownloadCSV = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const blob = await downloadMutationCSV(projectSlug, collectionSlug);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${collectionSlug || 'collection'}-mutable-nfts.csv`;
      a.click();
      URL.revokeObjectURL(url);
      showSuccess('CSV downloaded');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to download CSV');
    } finally {
      setDownloading(false);
    }
  };

  // Handle CSV file selection — parse client-side to detect image_filename column
  const handleCSVSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      showError('Please select a CSV file');
      return;
    }

    setCsvFile(file);
    imageUpload.reset();
    setCsvImageFilenames([]);
    setCsvRowCount(0);

    // Parse CSV to extract image_filename values
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      if (!text) return;

      // Proper CSV parser that handles quoted fields with commas
      const parseCSVRow = (row: string): string[] => {
        const cols: string[] = [];
        let i = 0;
        while (i <= row.length) {
          if (i === row.length) { cols.push(''); break; }
          if (row[i] === '"') {
            // Quoted field — find matching close quote
            let j = i + 1;
            let val = '';
            while (j < row.length) {
              if (row[j] === '"') {
                if (j + 1 < row.length && row[j + 1] === '"') {
                  val += '"';
                  j += 2;
                } else {
                  j++; // past closing quote
                  break;
                }
              } else {
                val += row[j];
                j++;
              }
            }
            cols.push(val);
            // Skip comma after quoted field
            if (j < row.length && row[j] === ',') j++;
            i = j;
          } else {
            // Unquoted field
            const nextComma = row.indexOf(',', i);
            if (nextComma === -1) {
              cols.push(row.slice(i));
              break;
            }
            cols.push(row.slice(i, nextComma));
            i = nextComma + 1;
          }
        }
        // Strip Excel ="..." formula wrappers from our Excel-safe CSV export
        return cols.map(v =>
          v.startsWith('="') && v.endsWith('"') ? v.slice(2, -1) : v
        );
      };

      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) return;

      const headers = parseCSVRow(lines[0]).map(h => h.trim().toLowerCase());
      const imgColIndex = headers.indexOf('image_filename');

      setCsvRowCount(lines.length - 1);

      if (imgColIndex === -1) {
        setCsvImageFilenames([]);
        return;
      }

      const filenames = new Set<string>();
      for (let i = 1; i < lines.length; i++) {
        const cols = parseCSVRow(lines[i]);
        const val = cols[imgColIndex]?.trim();
        if (val) filenames.add(val);
      }

      setCsvImageFilenames(Array.from(filenames).sort());
    };
    reader.readAsText(file);
  };

  // Create batch from CSV, then advance to image upload or preview
  const handleCreateBatch = async () => {
    if (!csvFile || creating) return;
    setCreating(true);
    try {
      const result = await createMutationBatch(projectSlug, collectionSlug, csvFile, {
        name: batchName || undefined,
        description: batchDescription || undefined,
      });
      setCreateResult(result);

      if (result.needsImages) {
        showSuccess(`Batch created: ${result.totalCount} NFTs — now select the required images`);
        // Stay on upload step for image selection
      } else {
        showSuccess(`Batch created with ${result.totalCount} NFTs`);
        setWizardStep('preview');
        loadPreview(result.batchId);
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to create batch');
    } finally {
      setCreating(false);
    }
  };

  // Load preview
  const loadPreview = async (batchId: string, page = 1) => {
    setPreviewLoading(true);
    try {
      const result = await getBatchPreview(projectSlug, collectionSlug, batchId, {
        page,
        limit: 50,
      });
      setPreviewItems(result.items);
      setPreviewPage(page);
      setPreviewTotal(result.totalCount);
    } catch (err) {
      showError('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Remove item from batch
  const handleRemoveItem = async (itemId: string) => {
    if (!createResult) return;
    try {
      await removeBatchItem(projectSlug, collectionSlug, createResult.batchId, itemId);
      setPreviewItems(prev => prev.filter(p => p.itemId !== itemId));
      setPreviewTotal(prev => prev - 1);
      setCreateResult(prev => prev ? { ...prev, totalCount: prev.totalCount - 1 } : null);
    } catch (err) {
      showError('Failed to remove item');
    }
  };

  // Submit batch
  const handleSubmitBatch = async () => {
    if (!createResult || submitting) return;
    setSubmitting(true);
    try {
      const options: any = {};
      if (scheduledAt) {
        options.scheduledAt = new Date(scheduledAt).toISOString();
        if (showCountdown) {
          options.showCountdown = true;
          if (countdownMessage) options.countdownMessage = countdownMessage;
        }
      }

      const result = await submitMutationBatch(
        projectSlug, collectionSlug, createResult.batchId, options
      );

      if (result.paymentRequired) {
        // Payment needed — initiate payment flow
        setPaymentBatchId(createResult.batchId);
        try {
          const paymentData = await createMutationFeePayment(
            projectSlug, collectionSlug, createResult.batchId
          );
          setFeePayload(paymentData);
          setShowPaymentModal(true);
        } catch (payErr) {
          showError(payErr instanceof Error ? payErr.message : 'Failed to create payment');
        }
        return; // Don't reset wizard yet — payment in progress
      }

      // Free batch — submitted directly
      if (result.scheduledAt) {
        showSuccess(`Batch scheduled for ${new Date(result.scheduledAt).toLocaleString()}`);
      } else {
        showSuccess(`Batch submitted for processing (~${estimatedDuration(result.totalCount)})`);
      }

      resetWizard();
      loadBatches();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to submit batch');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle payment success from XamanSigningModal
  const handlePaymentSuccess = async (txHash: string) => {
    if (!paymentBatchId || !feePayload) return;

    setShowPaymentModal(false);
    setWaitingForPayment(true);

    // Try backend verification first
    try {
      const status = await checkMutationFeePaymentStatus(
        projectSlug, collectionSlug, paymentBatchId,
        feePayload.payload?.uuid ?? feePayload.signingIntent?.intentId ?? ''
      );
      if (status.status === 'paid') {
        setWaitingForPayment(false);
        setFeePayload(null);
        setPaymentBatchId(null);
        showSuccess('Payment confirmed! Batch submitted for processing.');
        resetWizard();
        loadBatches();
        return;
      }
    } catch {
      // Fall through to polling
    }

    // Poll for confirmation
    const MAX_POLLS = 20;
    const POLL_INTERVAL_MS = 3000;

    const poll = async (count: number) => {
      if (count >= MAX_POLLS) {
        setWaitingForPayment(false);
        setFeePayload(null);
        setPaymentBatchId(null);
        showError('Payment verification timed out. Check the batch status — it may still be processing.');
        resetWizard();
        loadBatches();
        return;
      }

      try {
        const status = await checkMutationFeePaymentStatus(
          projectSlug, collectionSlug, paymentBatchId!,
          feePayload!.payload?.uuid ?? feePayload!.signingIntent?.intentId ?? ''
        );
        if (status.status === 'paid') {
          paymentPollingRef.current = null;
          setWaitingForPayment(false);
          setFeePayload(null);
          setPaymentBatchId(null);
          showSuccess('Payment confirmed! Batch submitted for processing.');
          resetWizard();
          loadBatches();
          return;
        }
      } catch {
        // Ignore and retry
      }

      paymentPollingRef.current = setTimeout(() => poll(count + 1), POLL_INTERVAL_MS);
    };

    paymentPollingRef.current = setTimeout(() => poll(0), POLL_INTERVAL_MS);
  };

  const handlePaymentRejected = () => {
    setShowPaymentModal(false);
    showError('Payment was rejected. You can retry from the batch list.');
    resetWizard();
    loadBatches();
  };

  const handlePaymentExpired = () => {
    setShowPaymentModal(false);
    showError('Payment request expired. You can retry from the batch list.');
    resetWizard();
    loadBatches();
  };

  const handlePaymentModalClose = () => {
    setShowPaymentModal(false);
  };

  // Handle "Pay Now" for pending_payment batches from the batch list
  const handlePayBatch = async (batchId: string) => {
    setPaymentBatchId(batchId);
    try {
      const paymentData = await createMutationFeePayment(projectSlug, collectionSlug, batchId);
      setFeePayload(paymentData);
      setShowPaymentModal(true);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to create payment');
    }
  };

  // Cleanup payment polling on unmount
  useEffect(() => {
    return () => {
      if (paymentPollingRef.current) {
        clearTimeout(paymentPollingRef.current);
      }
    };
  }, []);

  // Cancel batch
  const handleCancelBatch = async (batchId: string) => {
    if (!window.confirm('Are you sure you want to cancel this batch?')) return;
    try {
      await cancelMutationBatch(projectSlug, collectionSlug, batchId);
      showSuccess('Batch cancelled');
      loadBatches();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to cancel batch');
    }
  };

  // Resume batch
  const handleResumeBatch = async (batchId: string) => {
    try {
      await resumeMutationBatch(projectSlug, collectionSlug, batchId);
      showSuccess('Batch resumed');
      loadBatches();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to resume batch');
    }
  };

  // Pause batch
  const handlePauseBatch = async (batchId: string) => {
    try {
      await pauseMutationBatch(projectSlug, collectionSlug, batchId);
      showSuccess('Batch paused');
      loadBatches();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to pause batch');
    }
  };

  // Toggle item expansion
  const toggleExpand = (itemId: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const copyTokenId = (tokenId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(tokenId);
    setCopiedTokenId(tokenId);
    setTimeout(() => setCopiedTokenId(prev => prev === tokenId ? null : prev), 2000);
  };

  // Reset wizard
  const resetWizard = () => {
    setWizardOpen(false);
    setWizardStep('upload');
    setCsvFile(null);
    setBatchName('');
    setBatchDescription('');
    setCreating(false);
    setCreateResult(null);
    setCsvImageFilenames([]);
    setCsvRowCount(0);
    setShowImageListModal(false);
    imageUpload.reset();
    setPreviewItems([]);
    setPreviewPage(1);
    setPreviewTotal(0);
    setExpandedItems(new Set());
    setScheduledAt('');
    setShowCountdown(false);
    setCountdownMessage('');
    if (csvInputRef.current) csvInputRef.current.value = '';
  };

  return (
    <div className="space-y-6">
      {/* Actions bar */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-body-bold font-body-bold text-default-font">Bulk Mutations</h3>
          <p className="text-caption font-caption text-subtext-color mt-1">
            Update multiple NFTs at once via CSV upload
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="neutral-secondary"
            size="small"
            icon={<FeatherDownload className="h-4 w-4" />}
            onClick={handleDownloadCSV}
            loading={downloading}
          >
            Download CSV
          </Button>
          <Button
            variant="brand-primary"
            size="small"
            icon={<FeatherUpload className="h-4 w-4" />}
            onClick={() => {
              if (wizardOpen) resetWizard();
              setWizardOpen(true);
            }}
            disabled={wizardOpen}
          >
            New Bulk Mutation
          </Button>
        </div>
      </div>

      {/* Wizard */}
      {wizardOpen && (
        <div className="border border-neutral-border rounded-lg bg-default-background">
          {/* Wizard header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-border">
            <div className="flex items-center gap-4">
              <h4 className="text-body-bold font-body-bold text-default-font">New Bulk Mutation</h4>
              <div className="flex items-center gap-2">
                {(['upload', 'preview', 'schedule'] as WizardStep[]).map((step, i) => (
                  <React.Fragment key={step}>
                    {i > 0 && <div className="w-4 h-px bg-neutral-border" />}
                    <span className={`text-caption font-caption ${
                      wizardStep === step ? 'text-brand-600 font-bold' : 'text-subtext-color'
                    }`}>
                      {i + 1}. {step.charAt(0).toUpperCase() + step.slice(1)}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            </div>
            {wizardStep === 'upload' && !createResult && (
              <button onClick={resetWizard} className="p-1 text-subtext-color hover:text-default-font">
                <FeatherX className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Step: Upload */}
          {wizardStep === 'upload' && (
            <div className="p-6 space-y-4">
              <p className="text-body font-body text-subtext-color">
                Download the CSV above to get current NFT data, edit it, then upload it here.
                Only include rows for NFTs you want to mutate.
              </p>

              {/* Batch name/description (optional) */}
              <div className="grid grid-cols-2 gap-4 mobile:grid-cols-1">
                <div>
                  <label className="text-caption font-caption text-subtext-color block mb-1">Batch Name (optional)</label>
                  <input
                    type="text"
                    value={batchName}
                    onChange={e => setBatchName(e.target.value)}
                    placeholder="e.g., Season 2 Reveal"
                    className="w-full px-3 py-2 rounded-md border border-neutral-border bg-default-background text-default-font text-body font-body placeholder:text-subtext-color focus:outline-none focus:ring-2 focus:ring-brand-600"
                    maxLength={255}
                    disabled={!!createResult}
                  />
                </div>
                <div>
                  <label className="text-caption font-caption text-subtext-color block mb-1">Description (optional)</label>
                  <input
                    type="text"
                    value={batchDescription}
                    onChange={e => setBatchDescription(e.target.value)}
                    placeholder="e.g., Updating all mystery boxes with revealed art"
                    className="w-full px-3 py-2 rounded-md border border-neutral-border bg-default-background text-default-font text-body font-body placeholder:text-subtext-color focus:outline-none focus:ring-2 focus:ring-brand-600"
                    disabled={!!createResult}
                  />
                </div>
              </div>

              {/* Upload zones — stacked vertically */}
              <div className="space-y-4">
                {/* CSV upload zone */}
                <div>
                  <label className="text-caption font-caption text-subtext-color block mb-2">CSV Metadata</label>
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors min-h-[160px] flex items-center justify-center ${
                      csvFile
                        ? 'border-brand-600 bg-brand-50'
                        : 'border-neutral-border hover:border-brand-400'
                    } ${createResult ? 'pointer-events-none opacity-60' : ''}`}
                    onClick={() => !createResult && csvInputRef.current?.click()}
                  >
                    <input
                      ref={csvInputRef}
                      type="file"
                      accept=".csv"
                      onChange={handleCSVSelect}
                      className="hidden"
                    />
                    {csvFile ? (
                      <div>
                        <FeatherCheck className="h-6 w-6 mx-auto text-brand-600 mb-2" />
                        <p className="text-body font-body text-default-font">{csvFile.name}</p>
                        <p className="text-caption font-caption text-subtext-color mt-1">
                          {(csvFile.size / 1024).toFixed(1)} KB &middot; {csvRowCount} rows
                          {createResult && ` — ${createResult.totalCount} NFTs validated`}
                        </p>
                      </div>
                    ) : (
                      <div>
                        <FeatherUpload className="h-6 w-6 mx-auto text-subtext-color mb-2" />
                        <p className="text-body font-body text-default-font">Drop CSV here or click</p>
                        <p className="text-caption font-caption text-subtext-color mt-1">
                          Must have token_id and name columns.
                          {' '}If any rows have image_filename values, you'll upload those images next.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Images required banner + upload zone — shown after CSV validation when images are needed */}
                {createResult?.needsImages && (
                  <div className="space-y-3">
                    {/* Required images info banner */}
                    <div className="p-3 rounded-lg border border-brand-200 bg-brand-50 flex items-start gap-3">
                      <FeatherImage className="h-5 w-5 text-brand-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-body font-body text-default-font">
                          <strong>{csvImageFilenames.length}</strong> image file{csvImageFilenames.length !== 1 ? 's' : ''} required
                        </p>
                        <p className="text-caption font-caption text-subtext-color mt-0.5">
                          Select or drop files matching: {csvImageFilenames.slice(0, 5).map(f => (
                            <code key={f} className="bg-default-background px-1 py-0.5 rounded text-xs mx-0.5">{f}</code>
                          ))}{csvImageFilenames.length > 5 && (
                            <button
                              type="button"
                              onClick={() => setShowImageListModal(true)}
                              className="text-brand-600 hover:text-brand-700 underline underline-offset-2 ml-1"
                            >
                              and {csvImageFilenames.length - 5} more
                            </button>
                          )}
                        </p>
                      </div>
                      {csvImageFilenames.length > 5 && (
                        <button
                          type="button"
                          onClick={() => setShowImageListModal(true)}
                          className="text-caption font-caption text-brand-600 hover:text-brand-700 underline underline-offset-2 flex-shrink-0"
                        >
                          View all
                        </button>
                      )}
                    </div>
                    <div
                      className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors min-h-[120px] flex items-center justify-center ${
                        imageUpload.progress.phase === 'done'
                          ? 'border-brand-600 bg-brand-50'
                          : imageUpload.matchResult && imageUpload.matchResult.matched.size > 0
                            ? 'border-brand-600 bg-brand-50 cursor-pointer'
                            : 'border-neutral-border hover:border-brand-400 cursor-pointer'
                      } ${imageUpload.isUploading || imageUpload.progress.phase === 'done' ? 'pointer-events-none' : ''}`}
                      onClick={() => {
                        if (!imageUpload.isUploading && imageUpload.progress.phase !== 'done') {
                          imageUpload.inputRef.current?.click();
                        }
                      }}
                      onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                      onDrop={imageUpload.handleDrop}
                    >
                      <input
                        ref={imageUpload.inputRef}
                        type="file"
                        accept=".png,.jpg,.jpeg,.gif,.webp,.glb"
                        multiple
                        onChange={e => {
                          if (e.target.files) imageUpload.selectFiles(e.target.files);
                        }}
                        className="hidden"
                      />
                      {imageUpload.progress.phase === 'done' ? (
                        <div>
                          <FeatherCheck className="h-6 w-6 mx-auto text-brand-600 mb-2" />
                          <p className="text-body font-body text-default-font">
                            {imageUpload.progress.completedItems} images uploaded
                          </p>
                          {imageUpload.progress.failedItems > 0 && (
                            <p className="text-caption font-caption text-error-600 mt-1">
                              {imageUpload.progress.failedItems} failed
                            </p>
                          )}
                        </div>
                      ) : imageUpload.matchResult ? (
                        <div>
                          <FeatherImage className="h-6 w-6 mx-auto text-brand-600 mb-2" />
                          <p className="text-body font-body text-default-font">
                            {imageUpload.matchResult.matched.size} of {csvImageFilenames.length} files matched
                          </p>
                          {imageUpload.matchResult.missing.length > 0 && (
                            <p className="text-caption font-caption text-warning-600 mt-1">
                              {imageUpload.matchResult.missing.length} required file{imageUpload.matchResult.missing.length !== 1 ? 's' : ''} not found
                            </p>
                          )}
                          {imageUpload.matchResult.orphaned.length > 0 && (
                            <p className="text-caption font-caption text-subtext-color mt-1">
                              {imageUpload.matchResult.orphaned.length} extra file{imageUpload.matchResult.orphaned.length !== 1 ? 's' : ''} (will be ignored)
                            </p>
                          )}
                          <p className="text-caption font-caption text-subtext-color mt-1">
                            Click to re-select
                          </p>
                        </div>
                      ) : (
                        <div>
                          <FeatherImage className="h-6 w-6 mx-auto text-subtext-color mb-2" />
                          <p className="text-body font-body text-default-font">Select or drop image files</p>
                          <p className="text-caption font-caption text-subtext-color mt-1">
                            {csvImageFilenames.length} image file{csvImageFilenames.length !== 1 ? 's' : ''} required &middot; PNG, JPEG, GIF, WebP, GLB
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Upload progress bar */}
                    {imageUpload.isUploading && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-caption font-caption text-subtext-color">
                            {imageUpload.progress.statusMessage}
                          </p>
                          <button
                            onClick={imageUpload.cancelUpload}
                            className="text-caption font-caption text-error-600 hover:text-error-700"
                          >
                            Cancel
                          </button>
                        </div>
                        <div className="w-full bg-neutral-100 rounded-full h-2">
                          <div
                            className="bg-brand-600 rounded-full h-2 transition-all"
                            style={{ width: `${imageUpload.progress.percent}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Upload errors */}
                    {imageUpload.errors.length > 0 && !imageUpload.isUploading && (
                      <div className="p-3 rounded-md bg-error-50 border border-error-200">
                        <p className="text-caption font-caption text-error-700 font-bold mb-1">Upload errors:</p>
                        {imageUpload.errors.slice(0, 5).map((err, i) => (
                          <p key={i} className="text-caption font-caption text-error-600">{err}</p>
                        ))}
                        {imageUpload.errors.length > 5 && (
                          <p className="text-caption font-caption text-error-600">
                            ...and {imageUpload.errors.length - 5} more
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex justify-end gap-3">
                {!createResult ? (
                  <Button
                    variant="brand-primary"
                    onClick={handleCreateBatch}
                    disabled={!csvFile}
                    loading={creating}
                  >
                    {creating ? 'Validating CSV...' : 'Upload & Validate CSV'}
                  </Button>
                ) : createResult.needsImages && imageUpload.progress.phase !== 'done' ? (
                  <Button
                    variant="brand-primary"
                    onClick={imageUpload.startUpload}
                    disabled={!imageUpload.matchResult || imageUpload.matchResult.matched.size !== csvImageFilenames.length}
                    loading={imageUpload.isUploading}
                  >
                    Upload {imageUpload.matchResult ? imageUpload.matchResult.matched.size : 0} Images
                  </Button>
                ) : (
                  <Button
                    variant="brand-primary"
                    onClick={() => {
                      setWizardStep('preview');
                      loadPreview(createResult.batchId);
                    }}
                  >
                    Continue to Preview
                  </Button>
                )}
              </div>

              {/* Validation warnings from create result */}
              {createResult && createResult.validationWarnings.length > 0 && (
                <div className="p-3 rounded-md bg-warning-50 border border-warning-200">
                  <p className="text-caption font-caption text-warning-700 font-bold mb-1">Warnings:</p>
                  {createResult.validationWarnings.map((w, i) => (
                    <p key={i} className="text-caption font-caption text-warning-600">{w}</p>
                  ))}
                </div>
              )}

              {/* Required Images Modal */}
              {showImageListModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowImageListModal(false)}>
                  <div
                    className="bg-default-background border border-neutral-border rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col"
                    onClick={e => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-border">
                      <h3 className="text-body-bold font-body-bold text-default-font">
                        Required Image Files ({csvImageFilenames.length})
                      </h3>
                      <button onClick={() => setShowImageListModal(false)} className="p-1 text-subtext-color hover:text-default-font">
                        <FeatherX className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="px-6 py-4 space-y-3">
                      <div className="p-3 rounded-md bg-neutral-50 border border-neutral-border">
                        <div className="flex items-start gap-2">
                          <FeatherInfo className="h-4 w-4 text-brand-600 mt-0.5 flex-shrink-0" />
                          <div className="text-caption font-caption text-subtext-color space-y-1">
                            <p>Your CSV references <strong className="text-default-font">{csvImageFilenames.length}</strong> unique image filename{csvImageFilenames.length !== 1 ? 's' : ''}.</p>
                            <p>Select or drag-and-drop files with matching names. Filenames must match exactly (case-sensitive). Supported formats: PNG, JPEG, GIF, WebP, GLB.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 overflow-y-auto px-6 pb-4">
                      <div className="border border-neutral-border rounded-lg divide-y divide-neutral-border">
                        {csvImageFilenames.map((filename, i) => (
                          <div key={filename} className="flex items-center gap-3 px-3 py-2">
                            <span className="text-caption text-subtext-color w-6 text-right flex-shrink-0">{i + 1}</span>
                            <FeatherFile className="h-3.5 w-3.5 text-subtext-color flex-shrink-0" />
                            <span className="text-caption font-caption text-default-font font-mono truncate">{filename}</span>
                            {imageUpload.matchResult && (
                              imageUpload.matchResult.matched.has(filename)
                                ? <FeatherCheck className="h-3.5 w-3.5 text-success-600 flex-shrink-0 ml-auto" />
                                : <FeatherX className="h-3.5 w-3.5 text-error-600 flex-shrink-0 ml-auto" />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="px-6 py-4 border-t border-neutral-border">
                      <Button variant="neutral-secondary" onClick={() => setShowImageListModal(false)} className="w-full">
                        Close
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step: Preview */}
          {wizardStep === 'preview' && (
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-body font-body text-default-font">
                  <span className="font-bold">{previewTotal}</span> NFTs will be mutated
                  {createResult?.imageReferences
                    ? ` (${createResult.imageReferences} with image changes)`
                    : ' (metadata only)'}
                </p>
                <p className="text-caption font-caption text-subtext-color">
                  Est. processing: ~{estimatedDuration(previewTotal)}
                </p>
              </div>

              {previewLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner size="md" />
                </div>
              ) : (
                <div className="border border-neutral-border rounded-lg overflow-hidden">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-neutral-50 border-b border-neutral-border">
                        <th className="px-4 py-2 text-caption font-caption text-subtext-color w-8">#</th>
                        <th className="px-4 py-2 text-caption font-caption text-subtext-color">NFT Name</th>
                        <th className="px-4 py-2 text-caption font-caption text-subtext-color">Token ID</th>
                        <th className="px-4 py-2 text-caption font-caption text-subtext-color">Changes</th>
                        <th className="px-4 py-2 text-caption font-caption text-subtext-color w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewItems.map((item, i) => (
                        <React.Fragment key={item.itemId}>
                          <tr
                            className="border-b border-neutral-border hover:bg-default-background cursor-pointer"
                            onClick={() => toggleExpand(item.itemId)}
                          >
                            <td className="px-4 py-3 text-caption text-subtext-color">
                              {(previewPage - 1) * 50 + i + 1}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                {expandedItems.has(item.itemId)
                                  ? <FeatherChevronDown className="h-3 w-3 text-subtext-color flex-shrink-0" />
                                  : <FeatherChevronRight className="h-3 w-3 text-subtext-color flex-shrink-0" />}
                                <span className="text-body font-body text-default-font truncate">
                                  {item.newName}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1.5">
                                <span className="text-caption font-caption text-subtext-color font-mono truncate max-w-[180px]" title={item.nftTokenId}>
                                  {item.nftTokenId}
                                </span>
                                <button
                                  onClick={e => copyTokenId(item.nftTokenId, e)}
                                  className="p-0.5 text-subtext-color hover:text-default-font transition-colors flex-shrink-0"
                                  title="Copy token ID"
                                >
                                  {copiedTokenId === item.nftTokenId
                                    ? <FeatherCheck className="h-3 w-3 text-success-600" />
                                    : <FeatherCopy className="h-3 w-3" />}
                                </button>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {item.changes.map(c => (
                                  <Badge key={c} variant="neutral">{c}</Badge>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <button
                                onClick={e => { e.stopPropagation(); handleRemoveItem(item.itemId); }}
                                className="p-1 text-subtext-color hover:text-error-600 transition-colors"
                                title="Remove from batch"
                              >
                                <FeatherTrash2 className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          </tr>

                          {/* Expanded diff */}
                          {expandedItems.has(item.itemId) && (
                            <tr className="bg-neutral-50">
                              <td colSpan={5} className="px-8 py-4">
                                <div className="space-y-3 text-caption font-caption">
                                  {item.changes.includes('name') && (
                                    <div className="flex items-baseline gap-2">
                                      <span className="text-subtext-color w-24 flex-shrink-0">Name</span>
                                      <span className="text-error-600 line-through">{item.currentName}</span>
                                      <span className="text-subtext-color">&rarr;</span>
                                      <span className="text-success-600">{item.newName}</span>
                                    </div>
                                  )}
                                  {item.changes.includes('description') && (
                                    <div className="flex items-baseline gap-2">
                                      <span className="text-subtext-color w-24 flex-shrink-0">Description</span>
                                      <span className="text-error-600 line-through truncate max-w-[200px]">
                                        {item.currentDescription || '(empty)'}
                                      </span>
                                      <span className="text-subtext-color">&rarr;</span>
                                      <span className="text-success-600 truncate max-w-[200px]">
                                        {item.newDescription || '(empty)'}
                                      </span>
                                    </div>
                                  )}
                                  {item.changes.includes('image') && (
                                    <div className="flex items-baseline gap-2">
                                      <span className="text-subtext-color w-24 flex-shrink-0">Image</span>
                                      <span className="text-success-600">{item.newImageFilename}</span>
                                    </div>
                                  )}
                                  {item.changes.includes('traits') && (
                                    <>
                                      {item.traitDiff.changed.map(t => (
                                        <div key={t.trait} className="flex items-baseline gap-2">
                                          <span className="text-subtext-color w-24 flex-shrink-0">{t.trait}</span>
                                          <span className="text-error-600 line-through">{t.from}</span>
                                          <span className="text-subtext-color">&rarr;</span>
                                          <span className="text-success-600">{t.to}</span>
                                        </div>
                                      ))}
                                      {item.traitDiff.added.map(t => {
                                        const newVal = item.newTraits.find(tr => tr.trait_type === t)?.value;
                                        return (
                                          <div key={t} className="flex items-baseline gap-2">
                                            <span className="text-subtext-color w-24 flex-shrink-0">{t}</span>
                                            <span className="text-success-600">+ {newVal}</span>
                                          </div>
                                        );
                                      })}
                                      {item.traitDiff.removed.map(t => {
                                        const oldVal = item.currentTraits.find(tr => tr.trait_type === t)?.value;
                                        return (
                                          <div key={t} className="flex items-baseline gap-2">
                                            <span className="text-subtext-color w-24 flex-shrink-0">{t}</span>
                                            <span className="text-error-600">- {oldVal}</span>
                                          </div>
                                        );
                                      })}
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {previewTotal > 50 && (
                <div className="flex items-center justify-center gap-2">
                  <Button
                    variant="neutral-secondary"
                    size="small"
                    disabled={previewPage <= 1}
                    onClick={() => createResult && loadPreview(createResult.batchId, previewPage - 1)}
                  >
                    Previous
                  </Button>
                  <span className="text-caption font-caption text-subtext-color">
                    Page {previewPage} of {Math.ceil(previewTotal / 50)}
                  </span>
                  <Button
                    variant="neutral-secondary"
                    size="small"
                    disabled={previewPage >= Math.ceil(previewTotal / 50)}
                    onClick={() => createResult && loadPreview(createResult.batchId, previewPage + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}

              <div className="flex justify-between">
                <Button variant="neutral-secondary" onClick={() => setWizardStep('upload')}>
                  Back
                </Button>
                <Button
                  variant="brand-primary"
                  onClick={() => setWizardStep('schedule')}
                  disabled={previewTotal === 0}
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* Step: Schedule / Submit */}
          {wizardStep === 'schedule' && (
            <div className="p-6 space-y-4">
              <h4 className="text-body-bold font-body-bold text-default-font">
                Submit or Schedule Mutation
              </h4>
              <p className="text-body font-body text-subtext-color">
                {previewTotal} NFTs will be mutated. Estimated processing time: ~{estimatedDuration(previewTotal)}.
              </p>

              {/* Schedule option */}
              <div className="border border-neutral-border rounded-lg p-4 space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!scheduledAt}
                    onChange={e => {
                      if (!e.target.checked) {
                        setScheduledAt('');
                        setShowCountdown(false);
                        setCountdownMessage('');
                      } else {
                        // Default to current local time (round up to next 5 min)
                        const d = new Date();
                        d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5, 0, 0);
                        const local = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
                        setScheduledAt(local);
                      }
                    }}
                    className="rounded border-neutral-border"
                  />
                  <span className="text-body font-body text-default-font">Schedule for later</span>
                </label>

                {scheduledAt && (
                  <>
                    <div>
                      <label className="text-caption font-caption text-subtext-color block mb-1">
                        Date & Time
                      </label>
                      <input
                        type="datetime-local"
                        value={scheduledAt}
                        onChange={e => setScheduledAt(e.target.value)}
                        className="px-3 py-2 rounded-md border border-neutral-border bg-default-background text-default-font text-body font-body focus:outline-none focus:ring-2 focus:ring-brand-600"
                      />
                    </div>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showCountdown}
                        onChange={e => setShowCountdown(e.target.checked)}
                        className="rounded border-neutral-border"
                      />
                      <span className="text-body font-body text-default-font">
                        Show countdown on collection page
                      </span>
                    </label>

                    {showCountdown && (
                      <div>
                        <label className="text-caption font-caption text-subtext-color block mb-1">
                          Countdown Message
                        </label>
                        <input
                          type="text"
                          value={countdownMessage}
                          onChange={e => setCountdownMessage(e.target.value)}
                          placeholder="e.g., The Great Reveal"
                          maxLength={200}
                          className="w-full px-3 py-2 rounded-md border border-neutral-border bg-default-background text-default-font text-body font-body placeholder:text-subtext-color focus:outline-none focus:ring-2 focus:ring-brand-600"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Fee display */}
              {(() => {
                const fee = calculateMutationFeeClient(previewTotal);
                if (fee.feeXrp > 0) {
                  return (
                    <div className="border border-neutral-border rounded-lg p-4 bg-default-background">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-body font-body text-default-font font-semibold">Platform Fee</span>
                          <p className="text-caption font-caption text-subtext-color mt-1">
                            First {fee.freeTier} NFTs free, then {fee.perNftFeeXrp} XRP per NFT ({fee.billableCount} billable)
                          </p>
                        </div>
                        <span className="text-heading-3 font-heading-3 text-default-font">
                          {fee.feeXrp} XRP
                        </span>
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="border border-neutral-border rounded-lg p-4 bg-default-background">
                    <div className="flex items-center gap-2">
                      <FeatherCheck className="h-4 w-4 text-success-600" />
                      <span className="text-body font-body text-default-font">No fee — batch is within the free tier ({fee.freeTier} NFTs)</span>
                    </div>
                  </div>
                );
              })()}

              <div className="flex justify-between">
                <Button variant="neutral-secondary" onClick={() => setWizardStep('preview')}>
                  Back
                </Button>
                <Button
                  variant="brand-primary"
                  onClick={handleSubmitBatch}
                  loading={submitting}
                >
                  {(() => {
                    const fee = calculateMutationFeeClient(previewTotal);
                    if (fee.feeXrp > 0) {
                      return scheduledAt ? `Pay ${fee.feeXrp} XRP & Schedule` : `Pay ${fee.feeXrp} XRP & Mutate`;
                    }
                    return scheduledAt ? 'Schedule Mutation' : 'Mutate Now';
                  })()}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Batch list — only show actionable/completed batches */}
      {(() => {
        const visibleBatches = batches.filter(b =>
          ['pending_payment', 'processing', 'scheduled', 'paused', 'completed', 'failed'].includes(b.status)
        );
        return (
      <div>
        <h4 className="text-body-bold font-body-bold text-default-font mb-3">Mutation History</h4>

        {batchesLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="md" />
          </div>
        ) : visibleBatches.length === 0 ? (
          <div className="text-center py-8 border border-neutral-border rounded-lg bg-default-background">
            <FeatherClock className="h-8 w-8 mx-auto text-subtext-color mb-2" />
            <p className="text-body font-body text-subtext-color">No bulk mutations yet</p>
            <p className="text-caption font-caption text-subtext-color mt-1">
              Download the CSV, make changes, and upload to get started
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {visibleBatches.map(batch => {
              const statusInfo = STATUS_BADGES[batch.status] || { variant: 'neutral', label: batch.status };
              const progress = batch.total_count > 0
                ? Math.round(((batch.completed_count + batch.failed_count) / batch.total_count) * 100)
                : 0;

              return (
                <div
                  key={batch.id}
                  className="border border-neutral-border rounded-lg p-4 bg-default-background"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Badge variant={statusInfo.variant as any}>{statusInfo.label}</Badge>
                      <span className="text-body font-body text-default-font">
                        {batch.name || `Batch from ${formatTimeAgo(batch.created_at)}`}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {batch.status === 'pending_payment' && (
                        <Button
                          variant="brand-primary"
                          size="small"
                          onClick={() => handlePayBatch(batch.id)}
                        >
                          Pay {batch.payment_amount_drops
                            ? `${(Number(batch.payment_amount_drops) / 1_000_000).toFixed(1)} XRP`
                            : 'Now'}
                        </Button>
                      )}
                      {batch.status === 'processing' && (
                        <Button
                          variant="neutral-secondary"
                          size="small"
                          icon={<FeatherPause className="h-3.5 w-3.5" />}
                          onClick={() => handlePauseBatch(batch.id)}
                        >
                          Pause
                        </Button>
                      )}
                      {batch.status === 'paused' && (
                        <Button
                          variant="brand-secondary"
                          size="small"
                          icon={<FeatherPlay className="h-3.5 w-3.5" />}
                          onClick={() => handleResumeBatch(batch.id)}
                        >
                          Resume
                        </Button>
                      )}
                      {['preparing', 'ready', 'pending_payment', 'scheduled', 'paused'].includes(batch.status) && (
                        <Button
                          variant="destructive-secondary"
                          size="small"
                          icon={<FeatherX className="h-3.5 w-3.5" />}
                          onClick={() => handleCancelBatch(batch.id)}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="mt-2 flex items-center gap-4 text-caption font-caption text-subtext-color">
                    <span>{batch.total_count} NFTs</span>
                    {(batch.status === 'processing' || batch.status === 'paused') && (
                      <>
                        <span>{batch.completed_count + batch.failed_count}/{batch.total_count} processed ({progress}%)</span>
                        {batch.failed_count > 0 && (
                          <span className="text-error-600">{batch.failed_count} failed</span>
                        )}
                        {batch.status === 'processing' && batch.total_count > 0 && batch.completed_count + batch.failed_count > 0 && (() => {
                          const remaining = batch.total_count - batch.completed_count - batch.failed_count;
                          return remaining > 0 ? (
                            <span>~{estimatedDuration(remaining)} remaining</span>
                          ) : null;
                        })()}
                      </>
                    )}
                    {batch.status === 'completed' && (
                      <span className="text-success-600">
                        {batch.completed_count} completed
                        {batch.failed_count > 0 && `, ${batch.failed_count} failed`}
                      </span>
                    )}
                    {batch.status === 'failed' && (
                      <span className="text-error-600">
                        {batch.completed_count} completed, {batch.failed_count} failed
                      </span>
                    )}
                    {batch.status === 'pending_payment' && batch.payment_amount_drops && (
                      <span className="text-warning-600">
                        Fee: {(Number(batch.payment_amount_drops) / 1_000_000).toFixed(1)} XRP
                      </span>
                    )}
                    {batch.scheduled_at && batch.status === 'scheduled' && (
                      <span>Scheduled: {new Date(batch.scheduled_at).toLocaleString()}</span>
                    )}
                    <span>{formatTimeAgo(batch.created_at)}</span>
                  </div>

                  {/* Auto-pause alert */}
                  {batch.status === 'paused' && batch.auto_pause_reason && (
                    <div className="mt-2 p-2.5 rounded-md bg-error-50 border border-error-200 flex items-start gap-2">
                      <FeatherAlertTriangle className="h-4 w-4 text-error-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-caption font-caption text-error-700 font-bold">Auto-paused</p>
                        <p className="text-caption font-caption text-error-600">{batch.auto_pause_reason}</p>
                      </div>
                    </div>
                  )}

                  {/* Progress bar for processing/paused batches */}
                  {(batch.status === 'processing' || batch.status === 'paused') && (
                    <div className="mt-2">
                      <div className="w-full bg-neutral-100 rounded-full h-1.5">
                        <div
                          className={`rounded-full h-1.5 transition-all ${
                            batch.status === 'paused' ? 'bg-warning-500' : 'bg-brand-600'
                          }`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
        );
      })()}

      {/* Payment Modal */}
      {showPaymentModal && feePayload && (
        <WalletSigningModal
          open={showPaymentModal}
          onClose={handlePaymentModalClose}
          title="Pay Mutation Fee"
          payload={toSigningPayload(feePayload.signingIntent ?? feePayload.payload)}
          onSuccess={handlePaymentSuccess}
          onRejected={handlePaymentRejected}
          onExpired={handlePaymentExpired}
          message={`${feePayload.fee.feeXrp} XRP for ${feePayload.fee.totalItems} NFT mutations (${feePayload.fee.billableCount} billable)`}
        />
      )}

      {/* Payment Confirmation Waiting Overlay */}
      {waitingForPayment && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-default-background rounded-xl p-8 max-w-md mx-4 text-center">
            <div className="flex justify-center mb-4">
              <LoadingSpinner />
            </div>
            <h3 className="text-heading-3 font-heading-3 text-default-font mb-2">
              Confirming Payment
            </h3>
            <p className="text-body font-body text-subtext-color">
              Verifying your transaction on XRPL. This usually takes a few seconds...
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
